import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { WalletManager } from '../../managers/wallet.manager';
import { Kcc20AdminApiService } from '../../services/admin/kcc20-admin-api.service';
import {
  KcIconComponent,
  KcInputComponent,
  KcTooltipDirective,
  ResponsiveService,
} from '@kaspacom/ui-kit';
import { TokenSearchModalComponent } from '../token-search-modal/token-search-modal.component';
import { environment } from '../../../environments/environment';

interface MenuItem {
  label: string;
  value: string;
  iconClass: string;
  path: string;
  queryParams?: Record<string, string>;
  badge?: string;
  visible?: boolean;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

@Component({
  selector: 'kcc20-defi-left-sidebar',
  standalone: true,
  imports: [RouterLink, KcIconComponent, KcInputComponent, KcTooltipDirective],
  templateUrl: './defi-left-sidebar.component.html',
  styleUrl: './defi-left-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefiLeftSidebarComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly walletManager = inject(WalletManager);
  private readonly adminApi = inject(Kcc20AdminApiService);
  private readonly dialog = inject(Dialog);
  protected readonly responsiveService = inject(ResponsiveService);
  private walletChanges?: Subscription;
  private adminCheckSequence = 0;

  readonly sidebarOpen = input<boolean>(false);
  readonly sidebarToggle = output<boolean>();

  protected readonly currentUrl = signal(this.router.url);
  protected readonly showAdminNav = signal(false);
  protected readonly showMigrationAdminNav = signal(false);
  protected readonly isMobile = computed(
    () => this.responsiveService.deviceType() === 'mobile',
  );

  // Grouped IA, mirroring the design handoff rail (TRADE / ACCOUNT /
  // BUILD / PLATFORM). Items with `visible: false` are filtered out and
  // empty groups are dropped so the collapsed (icon) rail stays clean.
  protected readonly menuGroups = computed<MenuGroup[]>(() => {
    const groups: MenuGroup[] = [
      {
        label: 'Trade',
        items: [
          {
            label: 'Markets',
            value: 'markets',
            iconClass: 'analytics',
            path: '/markets',
          },
          {
            label: 'Mints',
            value: 'mints',
            iconClass: 'minde',
            path: '/mints',
            badge: 'New',
          },
        ],
      },
      {
        label: 'Account',
        items: [
          {
            label: 'Portfolio',
            value: 'portfolio',
            iconClass: 'coins-02',
            path: '/portfolio',
          },
          {
            label: 'Rewards',
            value: 'rewards',
            iconClass: 'gift',
            path: '/rewards',
            badge: 'New',
          },
        ],
      },
      {
        label: 'Build',
        items: [
          {
            label: 'Deploy token',
            value: 'deploy-token',
            iconClass: 'minde',
            path: '/deploy-token',
          },
          {
            label: 'Import token',
            value: 'import-token',
            iconClass: 'minde',
            path: '/import-token',
          },
          {
            label: 'Migrations',
            value: 'migrations',
            iconClass: 'transfer-tokens',
            path: '/migrations',
            visible: environment.krc20SnapshotMigrationEnabled,
          },
          // Token admin is intentionally not a global menu item: it is scoped
          // to a single token you own and is reached via the "Manage" button on
          // that token's page (/tokens/:covenantId/manage).
        ],
      },
      {
        label: 'Platform',
        items: [
          {
            label: 'Migration admin',
            value: 'migration-admin',
            iconClass: 'shield',
            path: '/migration-admin',
            visible: this.showMigrationAdminNav(),
          },
          {
            label: 'Admin',
            value: 'admin',
            iconClass: 'shield',
            path: '/admin',
            visible: this.showAdminNav(),
          },
          {
            label: 'Help / FAQ',
            value: 'faq',
            iconClass: 'help-circle',
            path: '/faq',
          },
        ],
      },
    ];

    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.visible !== false),
      }))
      .filter((group) => group.items.length > 0);
  });

  // Flat list of all visible items — used by the collapsed (icon) rail.
  protected readonly menuItems = computed<MenuItem[]>(() =>
    this.menuGroups().flatMap((group) => group.items),
  );

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
      });

    this.walletChanges = this.walletManager.onWalletChangedNotifier.subscribe(
      () => void this.refreshAdminNav(),
    );
    this.destroyRef.onDestroy(() => this.walletChanges?.unsubscribe());
    void this.refreshAdminNav();
  }

  protected openTokenSearchModal(): void {
    this.closeSidebar();
    this.dialog.open(TokenSearchModalComponent, {
      width: 'min(560px, 94vw)',
      height: 'min(600px, 90vh)',
      maxHeight: '90vh',
      // Modal focuses the search field itself after render.
      autoFocus: false,
    });
  }

  protected closeSidebar(): void {
    if (this.isMobile()) {
      this.sidebarToggle.emit(false);
    }
  }

  protected isMainItemSelected(mainItem: MenuItem): boolean {
    return this.isPathSelected(mainItem.path, this.currentUrl());
  }

  private isPathSelected(
    path: string | undefined,
    currentUrl: string,
  ): boolean {
    if (!path) return false;
    return currentUrl === path || currentUrl.startsWith(`${path}/`);
  }

  private async refreshAdminNav(): Promise<void> {
    const wallet = this.walletManager.signedWalletSignal();
    const checkId = ++this.adminCheckSequence;
    this.showAdminNav.set(false);
    this.showMigrationAdminNav.set(false);

    if (!wallet) return;

    try {
      const admin = await firstValueFrom(this.adminApi.getMe());
      if (checkId === this.adminCheckSequence) {
        this.showAdminNav.set(admin.isAdmin);
        this.showMigrationAdminNav.set(
          environment.krc20SnapshotMigrationEnabled &&
            admin.roles.some((role) =>
              ['SNAPSHOT_MIGRATOR', 'SYS_ADMIN'].includes(role),
            ),
        );
      }
    } catch {
      if (checkId === this.adminCheckSequence) {
        this.showAdminNav.set(false);
        this.showMigrationAdminNav.set(false);
      }
    }
  }
}
